// Tournament lifecycle transitions (pure, framework-free). A tournament moves
// DRAFT -> IN_PROGRESS -> FINISHED:
//
//   createDraft       build an empty DRAFT shell from a config (+ optional roster)
//   addPlayer         append a roster entry while DRAFT
//   removePlayer      drop a roster entry while DRAFT (re-seeds the remainder)
//   withdrawPlayer    retire a player from an IN_PROGRESS tournament (injury etc.)
//   startTournament   build the group stage + bracket and move to IN_PROGRESS
//   finishTournament  lock an IN_PROGRESS tournament once the bracket is decided
//
// Every function returns a brand-new state and never mutates its input. Invalid
// transitions throw explicit, actionable errors rather than failing silently.

import type {
  BracketNode,
  Group,
  Player,
  PlayerId,
  PlayerInput,
  PodiumEntry,
  Referee,
  SportId,
  TournamentConfig,
  TournamentState,
} from './types';
import {
  DEFAULT_COMPANY_ID,
  DEFAULT_TOURNAMENT_NAME,
  DEFAULT_TOURNAMENT_SPORT,
  PLAYER_MAX,
  PLAYER_MIN,
} from './types';
import { createTournament, type BuildOptions } from './build';
import {
  isCellId,
  isNodeId,
  playerId,
  playerKey,
  playerName,
  refereeId,
  slotId,
  tournamentEntityId,
} from './ids';
import { isPoolComplete, rankOverall, rankPool } from './standings';
import { normalizeTeamMembers } from './football';
import { recomputeNodes } from './resolve';

/** Options accepted when creating a draft. */
export interface CreateDraftOptions {
  /** Company governing the tournament; falls back to DEFAULT_COMPANY_ID. */
  companyId?: number;
  /** Sport being played; falls back to DEFAULT_TOURNAMENT_SPORT. */
  sportId?: SportId;
  /** Display name; falls back to DEFAULT_TOURNAMENT_NAME when blank. */
  name?: string;
  /** Free-text description shown on the public page before the start. */
  description?: string;
  /** Public URL of the cover image (GCS); null/undefined when none. */
  coverUrl?: string | null;
  /** Scheduled start (epoch ms); null/undefined when unscheduled. */
  startsAt?: number | null;
  /** Whether the public QR link accepts sign-ups; defaults to false. */
  acceptPublicRegistration?: boolean;
  /** Sign-up deadline (epoch ms); null/undefined when none. */
  registrationClosesAt?: number | null;
  /** Initial roster in seed order; defaults to an empty roster. */
  roster?: PlayerInput[];
}

/** Build a normalized Player from a roster entry at a 1-based seed. */
function playerFromInput(input: PlayerInput, seed: number): Player {
  const id = playerId(seed);
  return {
    id,
    playerKey: input.playerKey ?? playerKey(`seed${seed}`),
    name: input.name.trim() || playerName(seed),
    seed,
    members: normalizeTeamMembers(input.members),
  };
}

/** Roster (in seed order) derived from a state's players map. */
function rosterOf(state: TournamentState): PlayerInput[] {
  return Object.values(state.players)
    .sort((a, b) => a.seed - b.seed)
    .map((player) => ({
      name: player.name,
      playerKey: player.playerKey,
      members: player.members,
    }));
}

/** Rebuild the players map from a roster, assigning contiguous P1..Pn seeds. */
function playersFromRoster(roster: PlayerInput[]): Record<PlayerId, Player> {
  const players: Record<PlayerId, Player> = {};
  roster.forEach((input, index) => {
    const player = playerFromInput(input, index + 1);
    players[player.id] = player;
  });
  return players;
}

/**
 * Create an empty DRAFT tournament. The roster can be edited and the tournament
 * started later; the group stage and bracket are only built on `startTournament`.
 */
export function createDraft(
  config: TournamentConfig,
  options: CreateDraftOptions = {},
): TournamentState {
  const roster = options.roster ?? [];
  if (roster.length > PLAYER_MAX) {
    throw new Error(
      `Cannot create a draft with ${roster.length} players (max ${PLAYER_MAX}).`,
    );
  }
  const sportId = options.sportId ?? DEFAULT_TOURNAMENT_SPORT;
  // Football: single-game matches (no best-of) and no public self-registration
  // (sign-ups are individuals, not teams). Normalized here so a draft is
  // already coherent before it ever starts.
  const isFootball = sportId === 'FOOTBALL';
  const players = playersFromRoster(roster);
  return {
    id: tournamentEntityId(crypto.randomUUID()),
    companyId: options.companyId ?? DEFAULT_COMPANY_ID,
    sportId,
    name: options.name?.trim() || DEFAULT_TOURNAMENT_NAME,
    description: options.description?.trim() ?? '',
    coverUrl: options.coverUrl ?? null,
    startsAt: options.startsAt ?? null,
    acceptPublicRegistration: isFootball
      ? false
      : (options.acceptPublicRegistration ?? false),
    registrationClosesAt: isFootball
      ? null
      : (options.registrationClosesAt ?? null),
    status: 'DRAFT',
    config: isFootball
      ? {
          ...config,
          totalPlayers: roster.length,
          groupBestOf: 1,
          bracketBestOf: 1,
          finalBestOf: 1,
        }
      : { ...config, totalPlayers: roster.length },
    players,
    groups: {},
    cells: {},
    slots: {},
    nodes: {},
    referees: {},
    refereeSeq: 0,
    groupOrder: [],
    nodeOrder: [],
    timeSlotCount: 0,
    createdAt: Date.now(),
  };
}

/** Append a player to a DRAFT roster. */
export function addPlayer(
  state: TournamentState,
  input: PlayerInput,
): TournamentState {
  if (state.status !== 'DRAFT') {
    throw new Error('Players can only be added while the tournament is a draft.');
  }
  const roster = rosterOf(state);
  if (roster.length >= PLAYER_MAX) {
    throw new Error(`Cannot add more than ${PLAYER_MAX} players.`);
  }
  const nextRoster = [
    ...roster,
    { name: input.name, playerKey: input.playerKey, members: input.members },
  ];
  return {
    ...state,
    players: playersFromRoster(nextRoster),
    config: { ...state.config, totalPlayers: nextRoster.length },
  };
}

/** Remove a player from a DRAFT roster, re-seeding the remaining entries. */
export function removePlayer(
  state: TournamentState,
  id: PlayerId,
): TournamentState {
  if (state.status !== 'DRAFT') {
    throw new Error('Players can only be removed while the tournament is a draft.');
  }
  if (!state.players[id]) {
    throw new Error(`No such player to remove: ${id}.`);
  }
  const nextRoster = Object.values(state.players)
    .filter((player) => player.id !== id)
    .sort((a, b) => a.seed - b.seed)
    .map((player) => ({
      name: player.name,
      playerKey: player.playerKey,
      members: player.members,
    }));
  return {
    ...state,
    players: playersFromRoster(nextRoster),
    config: { ...state.config, totalPlayers: nextRoster.length },
  };
}

/** The editable fields of a DRAFT roster entry. */
export interface PlayerUpdate {
  name?: string;
  members?: string[];
}

/**
 * Edit a DRAFT roster entry in place — rename a participant or (for a team
 * sport) revise its squad member list. Unlike add/remove this never re-seeds:
 * the entry keeps its id, seed and stable `playerKey`.
 */
export function updatePlayer(
  state: TournamentState,
  id: PlayerId,
  update: PlayerUpdate,
): TournamentState {
  if (state.status !== 'DRAFT') {
    throw new Error('Players can only be edited while the tournament is a draft.');
  }
  const existing = state.players[id];
  if (!existing) {
    throw new Error(`No such player to update: ${id}.`);
  }
  const player: Player = {
    ...existing,
    name:
      update.name !== undefined
        ? update.name.trim() || playerName(existing.seed)
        : existing.name,
    members:
      update.members !== undefined
        ? normalizeTeamMembers(update.members)
        : existing.members,
  };
  return { ...state, players: { ...state.players, [id]: player } };
}

/**
 * Add a referee. Allowed while DRAFT or IN_PROGRESS — a local competition's
 * referee pool routinely changes between matchdays. Ids are allocated from
 * `refereeSeq`, the highest number ever used — never from the surviving map,
 * which forgets removed numbers — so a removed referee's id can never be
 * reissued to a different person. The max() guard self-heals a state persisted
 * before the counter existed (its column defaults to 0).
 */
export function addReferee(
  state: TournamentState,
  name: string,
): TournamentState {
  if (state.status === 'FINISHED') {
    throw new Error('Referees cannot be changed on a finished tournament.');
  }
  const trimmed = name.trim();
  if (!trimmed) {
    throw new Error('A referee needs a name.');
  }
  const highestCurrent = Object.values(state.referees).reduce(
    (max, r) => Math.max(max, r.seed),
    0,
  );
  const nextSeed = Math.max(state.refereeSeq, highestCurrent) + 1;
  const referee: Referee = { id: refereeId(nextSeed), name: trimmed, seed: nextSeed };
  return {
    ...state,
    referees: { ...state.referees, [referee.id]: referee },
    refereeSeq: nextSeed,
  };
}

/**
 * Remove a referee, clearing any match assignments they held. The remaining
 * referees keep their ids (see `addReferee`).
 */
export function removeReferee(
  state: TournamentState,
  refId: string,
): TournamentState {
  if (state.status === 'FINISHED') {
    throw new Error('Referees cannot be changed on a finished tournament.');
  }
  if (!state.referees[refId]) {
    throw new Error(`No such referee to remove: ${refId}.`);
  }
  const referees = { ...state.referees };
  delete referees[refId];

  let cells = state.cells;
  const clearedCells = Object.values(state.cells).filter(
    (cell) => cell.refereeId === refId,
  );
  if (clearedCells.length > 0) {
    cells = { ...state.cells };
    for (const cell of clearedCells) {
      cells[cell.id] = { ...cell, refereeId: null };
    }
  }

  let nodes = state.nodes;
  const clearedNodes = Object.values(state.nodes).filter(
    (node) => node.refereeId === refId,
  );
  if (clearedNodes.length > 0) {
    nodes = { ...state.nodes };
    for (const node of clearedNodes) {
      nodes[node.id] = { ...node, refereeId: null };
    }
  }

  return { ...state, referees, cells, nodes };
}

/**
 * Assign a referee to a match (a group cell or a bracket node), or clear the
 * assignment with null. Purely informational — the referee appears on the
 * match card and pre-fills the printed match-report form.
 */
export function assignReferee(
  state: TournamentState,
  matchId: string,
  refId: string | null,
): TournamentState {
  if (state.status === 'FINISHED') {
    throw new Error('Referees cannot be assigned on a finished tournament.');
  }
  if (refId !== null && !state.referees[refId]) {
    throw new Error(`No such referee to assign: ${refId}.`);
  }
  if (isCellId(matchId)) {
    const cell = state.cells[matchId];
    if (!cell) {
      throw new Error(`No such match to assign a referee to: ${matchId}.`);
    }
    if (cell.refereeId === refId) return state;
    return {
      ...state,
      cells: { ...state.cells, [matchId]: { ...cell, refereeId: refId } },
    };
  }
  if (isNodeId(matchId)) {
    const node = state.nodes[matchId];
    if (!node) {
      throw new Error(`No such match to assign a referee to: ${matchId}.`);
    }
    if (node.refereeId === refId) return state;
    return {
      ...state,
      nodes: { ...state.nodes, [matchId]: { ...node, refereeId: refId } },
    };
  }
  throw new Error(`Not a match id: ${matchId}.`);
}

/**
 * Withdraw a player from an IN_PROGRESS tournament — a mid-event retirement such
 * as an injury. Unlike `removePlayer`, which only edits a DRAFT roster and
 * re-seeds P1..Pn, a withdrawal must not disturb the named-block ids of the
 * players who stay, so their live scores and bracket progress survive untouched.
 *
 * The withdrawing player's involvement is annulled rather than scored as a
 * walkover: they are dropped from their group, every one of their group matches
 * (played or not) is deleted, and the pool's qualifiers are recomputed from the
 * players who remain. This mirrors how round-robin events usually treat a
 * retirement — keeping partial results would unfairly favour whoever happened to
 * have already played them. Any bracket seat, match or win the player held is
 * reopened (its score cleared) so the replacement qualifier can take their place
 * and the tournament stays fully playable.
 *
 * A group must keep at least two players so it still yields a 1st and 2nd
 * qualifier; withdrawing the second-to-last player of a group is refused.
 */
export function withdrawPlayer(
  state: TournamentState,
  id: PlayerId,
): TournamentState {
  if (state.status !== 'IN_PROGRESS') {
    throw new Error(
      'Players can only be withdrawn while the tournament is in progress.',
    );
  }
  if (!state.players[id]) {
    throw new Error(`No such player to withdraw: ${id}.`);
  }
  const group = Object.values(state.groups).find((g) =>
    g.playerIds.includes(id),
  );
  if (!group) {
    throw new Error(`Player ${id} is not assigned to any group.`);
  }
  if (group.playerIds.length <= 2) {
    throw new Error(
      `Cannot withdraw ${id}: group ${group.label} must keep at least two players.`,
    );
  }

  // 1. Drop the player from the roster.
  const players = { ...state.players };
  delete players[id];

  // 2. Delete all of the player's group matches and remove them from the pool.
  const removedCellIds = new Set(
    group.cellIds.filter((cid) => {
      const cell = state.cells[cid];
      return cell.playerA === id || cell.playerB === id;
    }),
  );
  const cells = { ...state.cells };
  for (const cid of removedCellIds) delete cells[cid];
  const nextGroup: Group = {
    ...group,
    playerIds: group.playerIds.filter((pid) => pid !== id),
    cellIds: group.cellIds.filter((cid) => !removedCellIds.has(cid)),
  };
  const groups = { ...state.groups, [group.id]: nextGroup };

  // 3. Recompute the affected pool's qualifiers, exactly as scoring a cell does:
  //    a now-complete pool promotes its top two survivors; an incomplete one
  //    clears its slots. This also evicts the withdrawn player from any slot.
  const poolCells = nextGroup.cellIds.map((cid) => cells[cid]);
  const slots = { ...state.slots };
  const firstId = slotId(group.label, 1);
  const secondId = slotId(group.label, 2);
  if (isPoolComplete(poolCells)) {
    const standings = rankPool(state.sportId, nextGroup.playerIds, poolCells);
    slots[firstId] = { ...slots[firstId], playerId: standings[0]?.playerId ?? null };
    slots[secondId] = { ...slots[secondId], playerId: standings[1]?.playerId ?? null };
  } else {
    slots[firstId] = { ...slots[firstId], playerId: null };
    slots[secondId] = { ...slots[secondId], playerId: null };
  }

  // 4. Reopen any bracket match the player had already entered or won so no stale
  //    score or winner lingers; recomputeNodes then re-resolves every node's
  //    participants from the refreshed slots, cascading the change downstream.
  const scrubbed: Record<string, BracketNode> = { ...state.nodes };
  for (const nodeKey of state.nodeOrder) {
    const node = scrubbed[nodeKey];
    if (node.playerA === id || node.playerB === id || node.winner === id) {
      scrubbed[nodeKey] = {
        ...node,
        games: [],
        events: [],
        status: 'PENDING',
        winner: null,
      };
    }
  }
  const resolved = recomputeNodes(state.nodeOrder, scrubbed, slots);

  // A downstream node reopened by the cascade (its feeder's winner vanished) is
  // reset to PENDING but keeps the score of the match that is no longer valid.
  // Drop it so the invariant "PENDING ⇔ no games recorded" holds everywhere.
  const nodes: Record<string, BracketNode> = {};
  for (const nodeKey of state.nodeOrder) {
    const node = resolved[nodeKey];
    nodes[nodeKey] =
      node.status === 'PENDING' && node.games.length > 0
        ? { ...node, games: [], events: [] }
        : node;
  }

  return {
    ...state,
    players,
    groups,
    cells,
    slots,
    nodes,
    config: { ...state.config, totalPlayers: state.config.totalPlayers - 1 },
  };
}

/**
 * Build the group stage + bracket for a DRAFT and move it to IN_PROGRESS. The
 * draft's roster (player names + stable keys) and creation timestamp are
 * preserved.
 */
export function startTournament(
  state: TournamentState,
  options: Omit<
    BuildOptions,
    | 'roster'
    | 'createdAt'
    | 'id'
    | 'companyId'
    | 'sportId'
    | 'referees'
    | 'refereeSeq'
    | 'name'
    | 'description'
    | 'coverUrl'
    | 'startsAt'
    | 'acceptPublicRegistration'
    | 'registrationClosesAt'
  > = {},
): TournamentState {
  if (state.status !== 'DRAFT') {
    throw new Error('Only a draft tournament can be started.');
  }
  const roster = rosterOf(state);
  if (roster.length < PLAYER_MIN || roster.length > PLAYER_MAX) {
    throw new Error(
      `A tournament needs ${PLAYER_MIN}\u2013${PLAYER_MAX} players to start (have ${roster.length}).`,
    );
  }
  return createTournament(state.config, {
    ...options,
    roster,
    id: state.id,
    companyId: state.companyId,
    sportId: state.sportId,
    referees: state.referees,
    refereeSeq: state.refereeSeq,
    name: state.name,
    description: state.description,
    coverUrl: state.coverUrl,
    startsAt: state.startsAt,
    acceptPublicRegistration: state.acceptPublicRegistration,
    registrationClosesAt: state.registrationClosesAt,
    createdAt: state.createdAt,
  });
}

/**
 * The bracket's final node: the match-1 node of the deepest main round. The
 * third-place playoff shares the final's round but is match 2, so keying on
 * `matchIndex === 1` disambiguates the two terminal nodes. Returns null for a
 * bracket-less (draft) state.
 */
export function finalNodeId(state: TournamentState): string | null {
  let bestId: string | null = null;
  let bestRound = 0;
  for (const id of state.nodeOrder) {
    const node = state.nodes[id];
    if (!node || node.round <= 0 || node.matchIndex !== 1) continue;
    if (node.round >= bestRound) {
      bestRound = node.round;
      bestId = id;
    }
  }
  return bestId;
}

/**
 * The third-place playoff node (match 2 of the final's round), or null when the
 * bracket has no semifinals (a single-final bracket has no bronze match).
 */
export function thirdPlaceNodeId(state: TournamentState): string | null {
  const finalId = finalNodeId(state);
  if (!finalId) return null;
  const finalRound = state.nodes[finalId].round;
  for (const id of state.nodeOrder) {
    const node = state.nodes[id];
    if (node && node.round === finalRound && node.matchIndex === 2) return id;
  }
  return null;
}

/**
 * True once the bracket is fully decided: the final has a winner and, when a
 * third-place playoff exists, it is decided too (so the full podium is known).
 */
export function isTournamentComplete(state: TournamentState): boolean {
  const finalId = finalNodeId(state);
  if (!finalId) return false;
  const final = state.nodes[finalId];
  if (final.status !== 'COMPLETED' || final.winner === null) return false;
  const bronzeId = thirdPlaceNodeId(state);
  if (bronzeId) {
    const bronze = state.nodes[bronzeId];
    if (bronze.status !== 'COMPLETED' || bronze.winner === null) return false;
  }
  return true;
}

/**
 * The final podium (1st/2nd/3rd) by bracket placement. 1st/2nd are the final's
 * winner and runner-up; 3rd is the third-place playoff winner. For a single-
 * final bracket (no bronze match) 3rd falls back to the best overall group-stage
 * finisher not already on the podium. Entries are null until decided.
 */
export function computePodium(state: TournamentState): PodiumEntry[] {
  const finalId = finalNodeId(state);
  const final = finalId ? state.nodes[finalId] : null;
  const champion = final?.winner ?? null;
  const runnerUp =
    final && final.winner !== null
      ? final.winner === final.playerA
        ? final.playerB
        : final.playerA
      : null;

  const bronzeId = thirdPlaceNodeId(state);
  let third: PlayerId | null = null;
  if (bronzeId) {
    third = state.nodes[bronzeId]?.winner ?? null;
  } else {
    const taken = new Set(
      [champion, runnerUp].filter((id): id is PlayerId => id !== null),
    );
    const overall = rankOverall(state);
    third = overall.find((s) => !taken.has(s.playerId))?.playerId ?? null;
  }

  return [
    { place: 1, playerId: champion },
    { place: 2, playerId: runnerUp },
    { place: 3, playerId: third },
  ];
}

/** Lock an IN_PROGRESS tournament once the bracket is fully decided. */
export function finishTournament(state: TournamentState): TournamentState {
  if (state.status !== 'IN_PROGRESS') {
    throw new Error('Only an in-progress tournament can be finished.');
  }
  if (!isTournamentComplete(state)) {
    throw new Error(
      'The tournament cannot be finished until the bracket is fully decided.',
    );
  }
  return { ...state, status: 'FINISHED' };
}
